// ═══════════════════════════════════════════════════════════════════
//  modules/console/Console.js
//  Console
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";

export function Console({ token }) {
  // Refonte : plus de sélecteur Spawn/Boxed ni de filtre Tout/Chat —
  // tout est relié via le chatbridge de Brice. Les commandes partent
  // sur "boxed" par défaut (visible partout). L'historique du chat est
  // chargé depuis la base au montage, puis le direct arrive en WebSocket.
  const [command, setCommand] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | boxchat | mp | global | cmd | conn | system
  const logsRef = useRef(null);

  // ── Canaux (comme le plugin chatbridge de Brice), chacun sa couleur ──
const CHANNELS = {
    boxchat: { label: "BoxChat", color: "#7ED957", bg: "#16351A" },
    mp:      { label: "MP", color: "#F0C420", bg: "#3A2D08" },
    global:  { label: "Général", color: "#BC8CFF", bg: "#2D1F4E" },
    discord: { label: "Discord", color: "#4F8EF7", bg: "#12294F" },
    cmd:     { label: "Commandes", color: "#FF8B3D", bg: "#3D2211" },
    conn:    { label: "Connexions", color: "#FFA987", bg: "#3B231A" },
    system:  { label: "Système", color: "#8B949E", bg: "#1A1F27" },
    error:   { label: "Erreurs", color: "#F85149", bg: "#3D1515" },
  };

  // Normalise le nom de canal envoyé par le plugin vers nos clés
  const normalizeChannel = (c, text) => {
    const v = String(c || "").toLowerCase();
    if (["boxchat", "box", "ile", "island"].includes(v)) return "boxchat";
    if (["mp", "msg", "tell", "private", "prive"].includes(v)) return "mp";
    if (["global", "general", "chat"].includes(v)) return "global";
    if (["discord", "taverne", "suggestion", "suggestions"].includes(v)) return "discord";
    if (["join", "quit", "connexion", "connection", "conn"].includes(v)) return "conn";
    // canal "system" du plugin : join/quit détectés par le contenu
    if (/joined the game|left the game|a rejoint|a quitté|s'est connecté|s'est déconnecté/i.test(text || "")) return "conn";
    return "system";
  };

  // Classifie une ligne brute des logs serveur
  const classifyLogLine = (line) => {
    if (/ERROR|SEVERE|FATAL|Exception|Stacktrace|\bWARN\b/i.test(line)) return "error";
    if (/joined the game|left the game|logged in|lost connection|Disconnect/i.test(line)) return "conn";
    if (line.includes("Async Chat Thread") || /:\s+\w+:\s/.test(line.split("]: ").pop() || "")) return "global";
    return "system";
  };

  // Historique du chat stocké en base (plugin BoxoriaChatPlugin)
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/chat/history?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          const historyLogs = data.messages.slice().reverse().map((m) => ({
            channel: normalizeChannel(m.channel || "global", m.message),
            text: `[${new Date(m.created_at).toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })}] ${m.username}${m.target ? ` → ${m.target}` : ""} : ${m.message}`,
          }));
          setLogs((prev) => [...historyLogs, ...prev]);
        }
      } catch (err) { console.error("Erreur historique chat", err); }
    };
    loadHistory();
  }, [token]);

  useEffect(() => {
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${wsProtocol}//${window.location.host}`);
    ws.onopen = () => console.log("WebSocket connecté");
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "chat") {
        // Message de chat diffusé par /api/chat/ingest (déjà stocké en base)
        setLogs((prev) => [...prev, {
          channel: normalizeChannel(data.channel, data.message),
          text: `[${new Date(data.created_at).toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })}] ${data.username}${data.target ? ` → ${data.target}` : ""} : ${data.message}`,
        }]);
      } else if (data.line) {
        setLogs((prev) => [...prev, { channel: classifyLogLine(data.line), text: data.line }]);
      }
    };
    ws.onerror = (err) => console.error("Erreur WebSocket", err);
    return () => ws.close();
  }, [token]);

  // Auto-scroll vers le bas quand de nouvelles lignes arrivent
  useEffect(() => {
    if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight;
  }, [logs, filter]);

  // Filtre par canal (les erreurs ont leur propre filtre)
  const visibleLogs = logs.filter((log) => {
    if (filter === "all") return true;
    return log.channel === filter;
  });

  const FILTERS = [
    { id: "all", label: "Tout", color: THEME.text, bg: THEME.panel },
    ...Object.entries(CHANNELS).map(([id, c]) => ({ id, label: c.label, color: c.color, bg: c.bg })),
  ];

  // Couleur d'une ligne selon son canal
  const lineColor = (log) => {
    if (log.type === "error") return THEME.red;
    if (log.type === "response") return THEME.emerald;
    return CHANNELS[log.channel]?.color || THEME.textDim;
  };

  const sendCommand = async () => {
    if (!command.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/console/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ command }),
      });
      const data = await res.json();
      if (res.ok) {
        const cleanResponse = data.response.replace(/§./g, "");
        setLogs((prev) => [...prev, { channel: "cmd", type: "cmd", text: `> ${command}` }, { channel: "cmd", type: "response", text: cleanResponse }]);
      } else {
        setLogs((prev) => [...prev, { channel: "error", type: "error", text: data.error || "Erreur" }]);
      }
    } catch {
      setLogs((prev) => [...prev, { channel: "error", type: "error", text: "Impossible de contacter le serveur" }]);
    }
    setCommand("");
    setLoading(false);
  };

  return (
    <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ color: THEME.text, fontSize: 14, fontWeight: 700, margin: 0 }}>🖥️ Console en direct</h3>
        <div style={{ display: "flex", gap: 8 }}>
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              style={{ background: filter === f.id ? f.bg : "transparent", border: `1px solid ${f.color}`, color: f.color, opacity: filter === f.id ? 1 : 0.55, borderRadius: 6, padding: "5px 14px", cursor: "pointer", fontSize: 12, fontWeight: filter === f.id ? 700 : 600 }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div ref={logsRef} style={{ background: "#0D1117", border: `1px solid ${THEME.border}`, borderRadius: 8, padding: 14, height: 320, overflowY: "auto", fontFamily: "'Courier New', monospace", fontSize: 12, marginBottom: 12 }}>
        {visibleLogs.length === 0 && <div style={{ color: THEME.textDim }}>Aucun message pour l'instant.</div>}
        {visibleLogs.map((log, i) => (
          <div key={i} style={{ color: lineColor(log), fontWeight: ["boxchat", "mp", "global", "discord"].includes(log.channel) ? 600 : 400, marginBottom: 4 }}>
            {log.text}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <input value={command} onChange={(e) => setCommand(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendCommand()}
          placeholder="Commande serveur... (ex: list)"
          style={{ flex: 1, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "8px 12px", color: THEME.text, fontSize: 13, fontFamily: "'Courier New', monospace", outline: "none" }} />
        <button onClick={sendCommand} disabled={loading}
          style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 6, padding: "8px 18px", cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, opacity: loading ? 0.6 : 1 }}>
          {loading ? "..." : "Envoyer ↵"}
        </button>
      </div>
    </div>
  );
}
